import {useEffect, useState} from 'react';
import {createContext, useContext, useReducer, ReactNode} from 'react';
import {
    Product,
    ProductVariation,
    CartItem,
    ShoppingState,
    ShoppingAction,
    CalendarCustomization,
    Department as ProductDepartment
} from '../types';
import {useProducts} from '../hooks/useProducts';
import {useCart} from '../hooks/useCart';
import {useUser} from './UserContext';
import {ImagePreloader} from '../services/imagePreloader';
import {CartService} from '../services/cartService';
import {DepartmentService} from '../services/departmentService';

const createInitialState = (products: Product[] = [], departments: ProductDepartment[] = []): ShoppingState => ({
    products,
    departments,
    cart: [],
    isCartOpen: false,
    quickViewProduct: null,
    isQuickViewOpen: false,
    toasts: [],
});

// Helper function to get cart item identifier
// Use server ID if available, otherwise construct from productId and variationId
function getCartItemId(item: CartItem): string {
    return item.id || `${item.productId}-${item.variationId}`;
}

// Reducer helper functions
function addToCart(state: ShoppingState, action: {
    product: Product;
    variation: ProductVariation;
    quantity: number;
    calendarCustomization?: CalendarCustomization
}): ShoppingState {
    const {product, variation, quantity, calendarCustomization} = action;
    const existingItemIndex = state.cart.findIndex(
        item => item.productId === product.id && item.variationId === String(variation.id) && !item.calendarCustomization
    );

    const toast = {
        id: `${Date.now()}-${product.id}`,
        message: 'Added to cart',
        productName: product.name,
        productImage: product.images[0],
    };

    if (existingItemIndex >= 0 && !calendarCustomization) {
        const updatedCart = [...state.cart];
        updatedCart[existingItemIndex].quantity += quantity;
        return {...state, cart: updatedCart, toasts: [...state.toasts, toast]};
    } else {
        const newCartItem: CartItem = {
            productId: product.id,
            variationId: String(variation.id),
            quantity,
            product,
            variation,
            calendarCustomization,
        };
        return {...state, cart: [...state.cart, newCartItem], toasts: [...state.toasts, toast]};
    }
}

function removeFromCart(state: ShoppingState, cartItemId: string): ShoppingState {
    return {
        ...state,
        cart: state.cart.filter(item => getCartItemId(item) !== cartItemId),
    };
}

function updateQuantity(state: ShoppingState, action: { cartItemId: string; quantity: number }): ShoppingState {
    const {cartItemId, quantity} = action;
    if (quantity <= 0) {
        return {
            ...state,
            cart: state.cart.filter(item => getCartItemId(item) !== cartItemId),
        };
    }
    return {
        ...state,
        cart: state.cart.map(item =>
            getCartItemId(item) === cartItemId
                ? {...item, quantity}
                : item
        ),
    };
}

function updateCalendarCustomization(state: ShoppingState, action: {
    cartItemId: string;
    calendarCustomization: CalendarCustomization
}): ShoppingState {
    const {cartItemId, calendarCustomization} = action;
    return {
        ...state,
        cart: state.cart.map(item =>
            getCartItemId(item) === cartItemId
                ? {...item, calendarCustomization}
                : item
        ),
    };
}

function shoppingReducer(state: ShoppingState, action: ShoppingAction): ShoppingState {
    switch (action.type) {
        case 'SET_PRODUCTS':
            return {...state, products: action.payload};
        case 'SET_DEPARTMENTS':
            return {...state, departments: action.payload};
        case 'SET_CART':
            return {...state, cart: action.payload};
        case 'ADD_TO_CART':
            return addToCart(state, action.payload);
        case 'REMOVE_FROM_CART':
            return removeFromCart(state, action.payload);
        case 'UPDATE_QUANTITY':
            return updateQuantity(state, action.payload);
        case 'UPDATE_CALENDAR_CUSTOMIZATION':
            return updateCalendarCustomization(state, action.payload);
        case 'TOGGLE_CART':
            return {...state, isCartOpen: !state.isCartOpen};
        case 'SET_CART_OPEN':
            return {...state, isCartOpen: action.payload};
        case 'OPEN_QUICK_VIEW':
            return {...state, quickViewProduct: action.payload, isQuickViewOpen: true};
        case 'CLOSE_QUICK_VIEW':
            return {...state, quickViewProduct: null, isQuickViewOpen: false};
        case 'SHOW_TOAST':
            return {...state, toasts: [...state.toasts, action.payload]};
        case 'REMOVE_TOAST':
            return {...state, toasts: state.toasts.filter(toast => toast.id !== action.payload)};
        case 'CLEAR_CART':
            return {
                ...state,
                cart: []
            };
        default:
            return state;
    }
}

const ShoppingContext = createContext<{
    state: ShoppingState;
    dispatch: (action: ShoppingAction) => Promise<void> | void;
    loading: boolean;
    error: string | null;
    currentPage: number;
    totalPages: number;
    total: number;
} | null>(null);

export function ShoppingProvider({
                                     children,
                                     initialProducts
                                 }: {
    children: ReactNode;
    initialProducts?: Product[];
}) {
    const {loading: userLoading} = useUser();
    const {products: fetchedProducts, loading, error, currentPage, totalPages, total} = useProducts({enabled: !userLoading});
    const products = initialProducts || fetchedProducts;
    const [departments, setDepartments] = useState<ProductDepartment[]>([]);
    const [departmentsLoading, setDepartmentsLoading] = useState(true);
    const [state, rawDispatch] = useReducer(shoppingReducer, createInitialState(products, departments));

    // Load departments after auth check completes
    useEffect(() => {
        if (userLoading) {
            return;
        }

        let isMounted = true;
        const loadDepartments = async () => {
            try {
                const fetchedDepartments = await DepartmentService.getDepartments();
                if (isMounted) {
                    setDepartments(fetchedDepartments);
                    setDepartmentsLoading(false);
                }
            } catch (error) {
                console.error('Failed to load departments:', error);
                if (isMounted) {
                    setDepartmentsLoading(false);
                }
            }
        };
        loadDepartments();

        // Listen for department updates (triggered by product search)
        const handleDepartmentsUpdated = (event: Event) => {
            const customEvent = event as CustomEvent;
            // Update departments even if detail is an empty array
            if (isMounted && customEvent.detail !== undefined) {
                setDepartments(customEvent.detail);
            }
        };

        window.addEventListener('departmentsUpdated', handleDepartmentsUpdated);

        return () => {
            isMounted = false;
            window.removeEventListener('departmentsUpdated', handleDepartmentsUpdated);
        };
    }, [userLoading]);

    // Load cart from server (server determines cart ID based on user authentication)
    const {cartItems, loading: cartLoading} = useCart(!userLoading);

    // Enhanced dispatch that syncs with server
    const dispatch = (action: ShoppingAction): Promise<void> | void => {
        // For actions that don't need server sync, handle synchronously
        switch (action.type) {
            case 'SET_PRODUCTS':
            case 'SET_DEPARTMENTS':
            case 'SET_CART':
            case 'UPDATE_CALENDAR_CUSTOMIZATION':
            case 'TOGGLE_CART':
            case 'SET_CART_OPEN':
            case 'OPEN_QUICK_VIEW':
            case 'CLOSE_QUICK_VIEW':
            case 'SHOW_TOAST':
            case 'REMOVE_TOAST':
                rawDispatch(action);
                return;
        }

        // For actions that need server sync, handle asynchronously
        // Dispatch to local state first
        rawDispatch(action);

        // Return the async operation for actions that need server sync
        return (async () => {
            try {
                switch (action.type) {
                    case 'ADD_TO_CART': {
                        const result = await CartService.addToCart(
                            action.payload.product.id,
                            action.payload.variation.id.toString(),
                            action.payload.quantity,
                            action.payload.calendarCustomization,
                            action.payload.variation.color,
                            action.payload.variation.size
                        );

                        // Show warning toast if there was a validation error
                        if (!result.success && result.warningMessage) {
                            rawDispatch({
                                type: 'SHOW_TOAST',
                                payload: {
                                    id: `${Date.now()}-warning`,
                                    message: result.warningMessage,
                                    productName: action.payload.product.name,
                                    productImage: action.payload.product.images[0],
                                    type: 'error',
                                },
                            });
                        }

                        // Refetch cart from server to synchronize state
                        const updatedCart = await CartService.getCart();
                        rawDispatch({type: 'SET_CART', payload: updatedCart});
                        break;
                    }

                    case 'UPDATE_QUANTITY': {
                        const {cartItemId, quantity} = action.payload;
                        // Use the cart item ID from the payload
                        const result = await CartService.updateCartItemQuantity(cartItemId, quantity);

                        // Show warning toast if there was a validation error
                        if (!result.success && result.warningMessage) {
                            rawDispatch({
                                type: 'SHOW_TOAST',
                                payload: {
                                    id: `${Date.now()}-warning`,
                                    message: result.warningMessage,
                                    productName: '', // Product info not available in this action
                                    productImage: '',
                                    type: 'error',
                                },
                            });
                        }

                        // Refetch cart from server to synchronize state
                        const updatedCartAfterUpdate = await CartService.getCart();
                        rawDispatch({type: 'SET_CART', payload: updatedCartAfterUpdate});
                        break;
                    }

                    case 'REMOVE_FROM_CART': {
                        const result = await CartService.removeFromCart(action.payload);

                        // Show warning toast if there was a validation error
                        if (!result.success && result.warningMessage) {
                            rawDispatch({
                                type: 'SHOW_TOAST',
                                payload: {
                                    id: `${Date.now()}-warning`,
                                    message: result.warningMessage,
                                    productName: '', // Product info not available in this action
                                    productImage: '',
                                    type: 'error',
                                },
                            });
                        }

                        // Refetch cart from server to synchronize state
                        const updatedCartAfterRemove = await CartService.getCart();
                        rawDispatch({type: 'SET_CART', payload: updatedCartAfterRemove});
                        break;
                    }

                    case 'CLEAR_CART': {
                        await CartService.clearCart();
                        rawDispatch({type: 'SET_CART', payload: []});
                        break;
                    }
                }
            } catch (error) {
                console.error('Failed to sync cart with server:', error);
                // Continue even if server sync fails - local state is already updated
            }
        })();
    };

    // Update products in state when they're loaded
    useEffect(() => {
        if (products.length > 0) {
            dispatch({type: 'SET_PRODUCTS', payload: products});

            // Preload all product images for better performance
            ImagePreloader.preloadProductImages(products).catch(error => {
                console.warn('Failed to preload some product images:', error);
            });
        }
    }, [products]);

    // Update departments in state when they're loaded
    // Always update, even if empty array (to show only "All Departments")
    useEffect(() => {
        dispatch({type: 'SET_DEPARTMENTS', payload: departments});
    }, [departments]);

    // Update cart in state when cart items are loaded from server
    useEffect(() => {
        if (!cartLoading) {
            dispatch({type: 'SET_CART', payload: cartItems});
        }
    }, [cartItems, cartLoading]);

    return (
        <ShoppingContext.Provider value={{
            state,
            dispatch,
            loading: loading || cartLoading || departmentsLoading,
            error,
            currentPage,
            totalPages,
            total
        }}>
            {children}
        </ShoppingContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useShopping() {
    const context = useContext(ShoppingContext);
    if (!context) {
        throw new Error('useShopping must be used within a ShoppingProvider');
    }
    return context;
}
